import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  Building2,
  Calendar,
  ChevronDown,
  Hash,
  LayoutGrid,
  List,
  Package,
  Search,
  SlidersHorizontal,
  Sparkles,
  Tag,
  X,
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import DivisionImg from '../assets/image/Division.jpg';

function formatDate(value) {
  if (!value) return '';
  return new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium' }).format(new Date(value));
}

/* ─── accent palette – one per division card ─── */
const ACCENTS = [
  { bar: 'from-teal-400 to-teal-600', icon: 'bg-teal-50 text-teal-600', badge: 'bg-teal-50 text-teal-700 border-teal-200', btn: 'bg-teal-600 hover:bg-teal-700', pill: 'bg-teal-600', ring: 'ring-teal-200' },
  { bar: 'from-blue-400 to-blue-600',   icon: 'bg-blue-50 text-blue-600',   badge: 'bg-blue-50 text-blue-700 border-blue-200',   btn: 'bg-blue-600 hover:bg-blue-700',   pill: 'bg-blue-600',   ring: 'ring-blue-200'   },
  { bar: 'from-violet-400 to-violet-600', icon: 'bg-violet-50 text-violet-600', badge: 'bg-violet-50 text-violet-700 border-violet-200', btn: 'bg-violet-600 hover:bg-violet-700', pill: 'bg-violet-600', ring: 'ring-violet-200' },
  { bar: 'from-amber-400 to-amber-600', icon: 'bg-amber-50 text-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-200', btn: 'bg-amber-600 hover:bg-amber-700', pill: 'bg-amber-600', ring: 'ring-amber-200' },
  { bar: 'from-rose-400 to-rose-600',   icon: 'bg-rose-50 text-rose-600',   badge: 'bg-rose-50 text-rose-700 border-rose-200',   btn: 'bg-rose-600 hover:bg-rose-700',   pill: 'bg-rose-600',   ring: 'ring-rose-200'   },
  { bar: 'from-emerald-400 to-emerald-600', icon: 'bg-emerald-50 text-emerald-600', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', btn: 'bg-emerald-600 hover:bg-emerald-700', pill: 'bg-emerald-600', ring: 'ring-emerald-200' },
];

/* ─── responsive grid column classes, shared everywhere ───
   mobile: 2 cols · sm: 2 cols · md: 3 cols · lg: 4 cols       */
const GRID_COLS = 'grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4';

/* ─── staggered fade-in wrapper ─── */
function FadeIn({ children, index = 0 }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), index * 70);
    return () => clearTimeout(t);
  }, [index]);
  return (
    <div
      style={{ opacity: show ? 1 : 0, transform: show ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.4s ease, transform 0.4s ease' }}
      className="min-w-0 w-full"
    >
      {children}
    </div>
  );
}

/* ══════════════════════════════════════════════
   DIVISION CARD (grid / list)
══════════════════════════════════════════════ */
function DivisionCard({ division, index, onSelect, view }) {
  const accent = ACCENTS[index % ACCENTS.length];

  if (view === 'list') {
    return (
      <article
        style={{ boxSizing: 'border-box' }}
        className={`group flex w-full min-w-0 items-center gap-2 overflow-hidden rounded-2xl border border-slate-200 bg-white px-3 py-3 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg ring-0 hover:ring-2 sm:gap-3 sm:px-4 ${accent.ring}`}
      >
        {/* colour strip */}
        <div className={`w-1 min-w-[4px] self-stretch rounded-full bg-gradient-to-b ${accent.bar} shrink-0`} />

        {/* icon */}
        <div className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${accent.icon} transition-transform group-hover:scale-110 sm:h-11 sm:w-11`}>
          <Building2 size={18} />
        </div>

        {/* text block — THIS must shrink and clip */}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5 overflow-hidden">
          {division.tag && (
            <span className={`inline-flex w-fit max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${accent.badge}`}>
              <Hash size={8} /><span className="truncate">{division.tag}</span>
            </span>
          )}
          <h2 className="w-full truncate text-sm font-black leading-tight text-slate-900 sm:text-base">{division.name}</h2>
          {division.description && (
            <p className="w-full truncate text-xs leading-relaxed text-slate-500">{division.description}</p>
          )}
        </div>

        {/* CTA button — never shrinks */}
        <button
          onClick={() => onSelect(division.id)}
          className={`ml-1 inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1.5 text-xs font-bold text-white shadow-md transition-all sm:gap-2 sm:px-4 sm:py-2 sm:text-sm ${accent.btn}`}
        >
          <span className="hidden sm:inline">Products</span>
          <ArrowRight size={13} />
        </button>
      </article>
    );
  }

  return (
    <article
      className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl ring-0 hover:ring-2 ${accent.ring} cursor-pointer`}
      onClick={() => onSelect(division.id)}
    >
      {/* top gradient bar */}
      <div className={`h-1.5 w-full bg-gradient-to-r ${accent.bar}`} />

      {/* big index watermark */}
      <span className="pointer-events-none absolute right-3 top-2 select-none text-4xl font-black text-slate-100 transition-all duration-300 group-hover:text-slate-200 group-hover:scale-110 sm:right-4 sm:top-3 sm:text-6xl">
        {String(index + 1).padStart(2, '0')}
      </span>

      <div className="flex flex-1 flex-col p-3.5 sm:p-6">
        <div className={`inline-grid h-10 w-10 shrink-0 place-items-center rounded-xl ${accent.icon} transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3 sm:h-12 sm:w-12`}>
          <Building2 size={20} className="sm:hidden" />
          <Building2 size={24} className="hidden sm:block" />
        </div>

        <div className="mt-3 flex flex-wrap gap-2 sm:mt-4">
          {division.tag && (
            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-widest sm:px-2.5 sm:text-[10px] ${accent.badge}`}>
              <Hash size={9} />{division.tag}
            </span>
          )}
        </div>

        <h2 className="mt-2 text-base font-black leading-tight text-slate-900 sm:text-xl">{division.name}</h2>

        {division.description ? (
          <p className="mt-1.5 line-clamp-2 text-xs leading-5 text-slate-500 sm:mt-2 sm:line-clamp-3 sm:text-sm sm:leading-6">{division.description}</p>
        ) : (
          <p className="mt-2 text-xs italic text-slate-300 sm:text-sm">No description provided.</p>
        )}

        <div className="my-3 h-px bg-gradient-to-r from-slate-100 via-slate-200 to-transparent sm:my-4" />

        <span className={`mt-auto inline-flex w-fit items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-bold text-white shadow transition-all group-hover:gap-2.5 sm:gap-2 sm:px-5 sm:py-2.5 sm:text-sm ${accent.btn}`}>
          <span className="sm:hidden">View</span>
          <span className="hidden sm:inline">View Products</span>
          <ArrowRight size={13} />
        </span>
      </div>

      {/* bottom sweep on hover */}
      <div className={`absolute bottom-0 left-0 h-0.5 w-0 bg-gradient-to-r ${accent.bar} transition-all duration-500 group-hover:w-full`} />
    </article>
  );
}

/* ══════════════════════════════════════════════
   PRODUCT CARD (inside a division)
══════════════════════════════════════════════ */
function ProductCard({ product, view }) {
  const firstImage = product.images?.[0];

  if (view === 'list') {
    return (
      <Link
        to={`/products/${product.id}`}
        className="group flex w-full min-w-0 items-center gap-3 overflow-hidden rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg hover:border-teal-300 sm:gap-4 sm:p-4"
      >
        <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-white sm:h-20 sm:w-20">
          {firstImage
            ? <img src={firstImage.url} alt={product.name} className="h-full w-full object-contain p-1.5 transition group-hover:scale-105 sm:p-2" />
            : <div className="grid h-full w-full place-items-center text-lg font-black text-teal-200">{product.name.slice(0, 2).toUpperCase()}</div>
          }
        </div>
        <div className="min-w-0 flex-1 overflow-hidden">
          {product.product_category_name && (
            <p className="truncate text-[10px] font-black uppercase tracking-widest text-teal-600">{product.product_category_name}</p>
          )}
          <h3 className="mt-0.5 truncate text-sm font-black text-slate-900 sm:text-base">{product.name}</h3>
          {product.formulation && <p className="mt-1 truncate text-xs text-slate-400">{product.formulation}</p>}
        </div>
        <ArrowRight size={15} className="shrink-0 text-slate-300 transition group-hover:text-teal-500 group-hover:translate-x-0.5" />
      </Link>
    );
  }

  return (
    <Link
      to={`/products/${product.id}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl hover:border-teal-300"
    >
      <div className="aspect-[4/3] w-full overflow-hidden bg-white">
        {firstImage
          ? <img src={firstImage.url} alt={product.name} className="h-full w-full object-contain p-2.5 transition duration-300 group-hover:scale-105 sm:p-4" />
          : <div className="grid h-full w-full place-items-center text-2xl font-black text-teal-200 sm:text-3xl">{product.name.slice(0, 2).toUpperCase()}</div>
        }
      </div>
      <div className="flex flex-1 flex-col p-3 sm:p-5">
        {product.product_category_name && (
          <p className="truncate text-[9px] font-black uppercase tracking-widest text-teal-600 sm:text-[10px]">{product.product_category_name}</p>
        )}
        <h3 className="mt-1 text-sm font-black leading-snug text-slate-900 sm:mt-1.5 sm:text-base">{product.name}</h3>
        {product.formulation && <p className="mt-1 text-xs text-slate-400 line-clamp-2 sm:mt-1.5">{product.formulation}</p>}
        <div className="mt-auto flex items-center gap-1 pt-2 text-xs font-semibold text-teal-600 opacity-0 transition sm:opacity-0 sm:group-hover:opacity-100">
          View details <ArrowRight size={11} />
        </div>
      </div>
    </Link>
  );
}

/* ══════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════ */
export default function Divisions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('division') || '';

  const [divisions, setDivisions]         = useState([]);
  const [categories, setCategories]       = useState([]);
  const [products, setProducts]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);
  const [error, setError]                 = useState('');
  const [productError, setProductError]   = useState('');

  /* filters */
  const [search, setSearch]   = useState('');
  const [category, setCategory] = useState('');
  const [sort, setSort]       = useState('order');
  const [view, setView]       = useState('grid');

  /* hero */
  const [heroVisible, setHeroVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setHeroVisible(true), 50); return () => clearTimeout(t); }, []);

  /* selected division object */
  const selectedDivision = divisions.find((d) => String(d.id) === String(selectedId));
  const selectedIndex    = divisions.findIndex((d) => String(d.id) === String(selectedId));
  const accent           = ACCENTS[selectedIndex >= 0 ? selectedIndex % ACCENTS.length : 0];

  /* load divisions + categories once */
  useEffect(() => {
    Promise.all([
      api.divisions({ page_size: 100 }),
      api.categories({ page_size: 100 }),
    ])
      .then(([divData, catData]) => {
        setDivisions(divData.results || []);
        setCategories(catData.results || []);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  /* reset filters when division changes */
  useEffect(() => { setCategory(''); setSearch(''); }, [selectedId]);

  /* load products when division / filters change */
  useEffect(() => {
    if (!selectedId) { setProducts([]); return; }
    const t = setTimeout(() => {
      setProductsLoading(true);
      api.products({ division: selectedId, category, search, page_size: 100 })
        .then((data) => { setProducts(data.results || []); setProductError(''); })
        .catch((err) => setProductError(err.message))
        .finally(() => setProductsLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [selectedId, category, search]);

  /* helpers */
  const goToDivision = (id) => setSearchParams({ division: id });
  const goToAll      = ()   => setSearchParams({});

  /* filtered & sorted divisions list */
  const filteredDivisions = useMemo(() => {
    let list = [...divisions];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((d) =>
        d.name.toLowerCase().includes(q) ||
        (d.tag || '').toLowerCase().includes(q) ||
        (d.description || '').toLowerCase().includes(q),
      );
    }
    list.sort(sort === 'date'
      ? (a, b) => new Date(b.created_at) - new Date(a.created_at)
      : (a, b) => a.name.localeCompare(b.name),
    );
    return list;
  }, [divisions, search, sort]);

  /* sorted products */
  const sortedProducts = useMemo(() => {
    const list = [...products];
    list.sort(sort === 'date'
      ? (a, b) => new Date(b.created_at) - new Date(a.created_at)
      : sort === 'name'
        ? (a, b) => a.name.localeCompare(b.name)
        : (a, b) => (a.order_no || 0) - (b.order_no || 0) || a.name.localeCompare(b.name),
    );
    return list;
  }, [products, sort]);

  const resultCount = selectedDivision ? sortedProducts.length : filteredDivisions.length;

  /* ─── DIVISION DETAIL VIEW ─── */
  if (selectedDivision) {
    return (
      <div className="min-h-screen w-full overflow-x-hidden bg-slate-50">

        {/* ── Division Hero ── */}
        <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
          {/* decorative blobs */}
          <div className={`pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-gradient-to-br ${accent.bar} opacity-20 blur-3xl`} />
          <div className="pointer-events-none absolute -bottom-16 left-1/3 h-64 w-64 rounded-full bg-blue-400/10 blur-2xl" />

          <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6">
            {/* back breadcrumb */}
            <button
              onClick={goToAll}
              className="mb-6 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/80 backdrop-blur-sm transition hover:bg-white/20"
            >
              ← All Divisions
            </button>

            <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:gap-8">
              {/* icon */}
              <div className={`grid h-16 w-16 shrink-0 place-items-center rounded-2xl ${accent.icon} ring-4 ring-white/10`}>
                <Building2 size={30} />
              </div>

              <div className="flex-1 min-w-0">
                {/* tag pill */}
                {selectedDivision.tag && (
                  <span className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-widest text-white/70 backdrop-blur-sm">
                    <Tag size={10} /> {selectedDivision.tag}
                  </span>
                )}

                <h1 className="text-4xl font-black leading-tight sm:text-5xl">{selectedDivision.name}</h1>

                {selectedDivision.description && (
                  <p className="mt-3 max-w-2xl text-base leading-7 text-white/70">{selectedDivision.description}</p>
                )}

                <div className="mt-4 flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 backdrop-blur-sm">
                    <Package size={16} className="text-white/60" />
                    <span className="text-lg font-black">{sortedProducts.length}</span>
                    <span className="text-xs font-semibold text-white/60">Products</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Sticky filter bar ── */}
        <section className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 px-3 py-3 backdrop-blur-md sm:px-6">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2 sm:gap-3">

            {/* search */}
            <div className="relative min-w-0 flex-1 basis-40">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search products..."
                className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-8 text-sm font-semibold text-slate-800 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X size={14} />
                </button>
              )}
            </div>

            {/* category filter */}
            <div className="relative min-w-0 flex-1 basis-32">
              <ChevronDown size={13} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full appearance-none rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-3 pr-7 text-sm font-semibold text-slate-700 outline-none focus:border-teal-400"
              >
                <option value="">All Categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* sort + view row */}
            <div className="flex shrink-0 items-center gap-2">
              {/* sort */}
              <div className="relative">
                <ChevronDown size={13} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="appearance-none rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-3 pr-7 text-sm font-semibold text-slate-700 outline-none focus:border-teal-400"
                >
                  <option value="order">Default Order</option>
                  <option value="name">A–Z</option>
                  <option value="date">Newest</option>
                </select>
              </div>

              {/* view toggle */}
              <div className="flex overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                <button onClick={() => setView('grid')} className={`px-3 py-2.5 transition ${view === 'grid' ? 'bg-teal-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>
                  <LayoutGrid size={15} />
                </button>
                <button onClick={() => setView('list')} className={`px-3 py-2.5 transition ${view === 'list' ? 'bg-teal-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>
                  <List size={15} />
                </button>
              </div>
            </div>

            <span className="w-full text-right text-xs font-semibold text-slate-400 sm:ml-auto sm:w-auto sm:text-sm">
              {resultCount} result{resultCount !== 1 ? 's' : ''}
            </span>
          </div>
        </section>

        {/* ── Products grid / list ── */}
        <section className="mx-auto w-full max-w-6xl overflow-hidden px-3 py-10 sm:px-6">
          {productError && (
            <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{productError}</div>
          )}

          {productsLoading ? (
            <div className={`grid w-full gap-3 sm:gap-4 ${view === 'grid' ? GRID_COLS : 'grid-cols-1'}`}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className={`animate-pulse rounded-2xl bg-white ${view === 'grid' ? 'h-56 sm:h-72' : 'h-20'}`} />
              ))}
            </div>
          ) : sortedProducts.length ? (
            <div className={`grid w-full gap-3 sm:gap-4 ${view === 'grid' ? GRID_COLS : 'grid-cols-1'}`}>
              {sortedProducts.map((product, i) => (
                <FadeIn key={product.id} index={i}>
                  <ProductCard product={product} view={view} />
                </FadeIn>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white p-16 text-center">
              <Package size={44} className="mx-auto text-slate-300" />
              <h2 className="mt-4 text-2xl font-bold text-slate-900">No products found</h2>
              <p className="mt-2 text-slate-500">Try changing the category filter or clearing the search.</p>
              <button
                onClick={() => { setCategory(''); setSearch(''); }}
                className="mt-6 rounded-full bg-teal-600 px-6 py-3 text-sm font-bold text-white hover:bg-teal-700 transition"
              >
                Clear Filters
              </button>
            </div>
          )}
        </section>
      </div>
    );
  }

  /* ─── ALL DIVISIONS VIEW ─── */
  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-slate-50">

      {/* ── Hero ── */}
      <img src={DivisionImg} alt="Divisions" className="w-full h-auto block" />

      {/* ── Sticky toolbar ── */}
      <section className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 px-3 py-3 backdrop-blur-md sm:px-6">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2 sm:gap-3">

          <div className="relative min-w-0 flex-1 basis-40">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search divisions..."
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-8 text-sm font-semibold text-slate-800 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X size={14} />
              </button>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <div className="relative">
              <ChevronDown size={13} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="appearance-none rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-3 pr-7 text-sm font-semibold text-slate-700 outline-none focus:border-teal-400"
              >
                <option value="order">Default Order</option>
                <option value="name">A–Z</option>
                <option value="date">Newest</option>
              </select>
            </div>

            <div className="flex overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
              <button onClick={() => setView('grid')} className={`px-3 py-2.5 transition ${view === 'grid' ? 'bg-teal-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>
                <LayoutGrid size={15} />
              </button>
              <button onClick={() => setView('list')} className={`px-3 py-2.5 transition ${view === 'list' ? 'bg-teal-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>
                <List size={15} />
              </button>
            </div>
          </div>

          <span className="w-full text-right text-xs font-semibold text-slate-400 sm:ml-auto sm:w-auto sm:text-sm">
            {resultCount} result{resultCount !== 1 ? 's' : ''}
          </span>
        </div>
      </section>

      {/* ── Divisions grid / list ── */}
      <section className="mx-auto w-full max-w-6xl overflow-hidden px-3 py-12 sm:px-6">
        {error && <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}

        {loading ? (
          <div className={`grid w-full gap-3 sm:gap-4 ${view === 'grid' ? GRID_COLS : 'grid-cols-1'}`}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-40 animate-pulse rounded-2xl bg-white sm:h-52" />
            ))}
          </div>
        ) : filteredDivisions.length ? (
          <div className={`grid w-full gap-3 sm:gap-4 ${view === 'grid' ? GRID_COLS : 'grid-cols-1'}`}>
            {filteredDivisions.map((division, i) => (
              <FadeIn key={division.id} index={i}>
                <DivisionCard
                  division={division}
                  index={divisions.indexOf(division)}
                  onSelect={goToDivision}
                  view={view}
                />
              </FadeIn>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-16 text-center">
            <Building2 size={40} className="mx-auto text-slate-300" />
            <h2 className="mt-4 text-xl font-bold text-slate-800">No divisions found</h2>
            <p className="mt-2 text-sm text-slate-400">Try a different search term.</p>
            <button
              onClick={() => setSearch('')}
              className="mt-5 rounded-full bg-teal-600 px-6 py-3 text-sm font-bold text-white hover:bg-teal-700 transition"
            >
              Clear Search
            </button>
          </div>
        )}
      </section>
    </div>
  );
}